import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, FileText, MapPin } from 'lucide-react';
import debtorApi from '../../api/debtorApi';
import debtorDocumentApi from '../../api/debtorDocumentApi';
import DebtorDetailHeader from '../../components/debtor/detail/DebtorDetailHeader';
import DebtorQuickStats from '../../components/debtor/detail/DebtorQuickStats';
import DebtorInfoTab from '../../components/debtor/detail/DebtorInfoTab';
import DebtorDocumentsTab from '../../components/debtor/detail/DebtorDocumentsTab';
import DebtorLocationTab from '../../components/debtor/detail/DebtorLocationTab';

const REQUIRED_DOCUMENTS = ['ktp', 'kk', 'npwp', 'slip_gaji', 'rekening_koran'];

const TABS = [
  { id: 'info', label: 'Informasi', icon: User },
  { id: 'documents', label: 'Dokumen', icon: FileText },
  { id: 'location', label: 'Lokasi', icon: MapPin },
];

export default function DebtorDetail() {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const debtorId = urlParams.get('id');

  const [debtor, setDebtor] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  const loadData = useCallback(async () => {
    try {
      const [debtorData, docsData] = await Promise.all([
        debtorApi.getById(parseInt(debtorId!)),
        debtorDocumentApi.getByDebtorId(parseInt(debtorId!)),
      ]);
      setDebtor(debtorData);
      setDocuments(docsData || []);
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  }, [debtorId]);

  useEffect(() => {
    if (debtorId) {
      loadData();
    }
  }, [debtorId, loadData]);

  const refreshDocuments = async () => {
    try {
      const docsData = await debtorDocumentApi.getByDebtorId(parseInt(debtorId!));
      setDocuments(docsData || []);
      // Reload debtor to get updated document_status
      const debtorData = await debtorApi.getById(parseInt(debtorId!));
      setDebtor(debtorData);
    } catch (error) {
      // Error refreshing documents
    }
  };

  const getDocumentCompleteness = () => {
    const docTypes = new Set(documents.map(d => d.document_type));
    const complete = REQUIRED_DOCUMENTS.filter(type => docTypes.has(type)).length;
    return {
      complete,
      total: REQUIRED_DOCUMENTS.length,
      percentage: Math.round((complete / REQUIRED_DOCUMENTS.length) * 100),
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!debtor) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Debitur tidak ditemukan</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
        >
          Kembali
        </button>
      </div>
    );
  }

  const completeness = getDocumentCompleteness();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'info':
        return <DebtorInfoTab debtor={debtor} />;
      case 'documents':
        return (
          <DebtorDocumentsTab
            debtorId={debtorId!}
            documents={documents}
            completeness={completeness}
            onRefresh={refreshDocuments}
          />
        );
      case 'location':
        return <DebtorLocationTab debtor={debtor} debtorId={debtorId!} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <DebtorDetailHeader debtor={debtor} debtorId={debtorId!} />

      {/* Quick Stats */}
      <DebtorQuickStats
        debtor={debtor}
        completeness={completeness}
        documentsCount={documents.length}
      />

      {/* Tabs */}
      <div className="space-y-6">
        {/* Tab Navigation */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const showIndicator = tab.id === 'documents' && completeness.percentage < 100;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-amber-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {showIndicator && <span className="w-2 h-2 bg-amber-500 rounded-full"></span>}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div key={activeTab} className="animate-fadeIn">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}
